import React, { useEffect, useMemo, useState } from "react";
import { Badge, DataTable } from "@netiv/ui-core";
import { X } from "lucide-react";
import { formatCompactAmount } from "./formatters";

type WorkflowEvent = {
  event_id: string;
  process_owner: string;
  event_type: string;
  status: string;
  expected_at?: string;
  occurred_at?: string;
  recorded_at: string;
  source_system: string;
  source_entity_id?: string;
  attributes: Record<string, unknown>;
  publication_status: string;
};
type StageDetail = {
  stage_detail_id: string;
  process_stage: string;
  document_id?: string;
  status: string;
  party_name?: string;
  party_country_code?: string;
  origin?: string;
  destination?: string;
  expected_at?: string;
  occurred_at?: string;
  amount?: number;
  currency?: string;
  incoterm?: string;
  booking_code?: string;
  measurements: Record<string, unknown>;
  details: Record<string, unknown>;
  source_system: string;
  source_entity_id?: string;
};
type Projection = {
  workflow: { workflow_id: string; category_id?: string; status: string; created_at: string };
  external_references: { source_system: string; source_entity_type: string; source_entity_id: string }[];
  events: WorkflowEvent[];
  stage_details: StageDetail[];
  exceptions: {
    exception_id: string; process_stage: string; exception_code: string; description?: string;
    severity: string; status: string; owner_team?: string; detected_at: string;
  }[];
  actions: {
    action_id: string; exception_id: string; action_type: string; status: string;
    assigned_team?: string; external_system?: string; external_ticket_id?: string;
    requested_at: string; acknowledged_at?: string; last_error?: string;
  }[];
};

const API_BASE = import.meta.env.VITE_API_GATEWAY_URL ?? import.meta.env.VITE_QUERY_API_URL ?? "";
const TABS = ["Summary", "Signal & Procurement", "Manufacturing", "Transport", "Packaging", "Shipment & PoD", "Invoice", "Exceptions"];

function matchesTab(event: WorkflowEvent, tab: string) {
  const value = `${event.process_owner} ${event.event_type}`.toLowerCase();
  if (tab === "Signal & Procurement") return /signal|procure|planned|purchase|order|po/.test(value);
  if (tab === "Manufacturing") return /manufactur/.test(value);
  if (tab === "Transport") return /transport|booking/.test(value);
  if (tab === "Packaging") return /packag|label/.test(value);
  if (tab === "Shipment & PoD") return /shipment|transit|pickup|delivery|pod|proof/.test(value);
  if (tab === "Invoice") return /invoice|credit|discount/.test(value);
  return true;
}

function eventRows(events: WorkflowEvent[]) {
  return events.map((event) => [
    <span style={{ fontWeight: 600 }}>{event.event_type}</span>,
    event.status,
    event.expected_at ? new Date(event.expected_at).toLocaleString() : "—",
    event.occurred_at ? new Date(event.occurred_at).toLocaleString() : "Planned",
    event.source_system,
    event.source_entity_id ?? "—",
    Object.keys(event.attributes ?? {}).length ? JSON.stringify(event.attributes) : "—",
  ]);
}

const TAB_STAGES: Record<string, string[]> = {
  "Signal & Procurement": ["signal", "planned_order", "purchase_order", "order"],
  "Manufacturing": ["manufacturing"],
  "Transport": ["transport_request"],
  "Packaging": ["packaging"],
  "Shipment & PoD": ["shipment", "pod"],
  "Invoice": ["invoice"],
};

function stageRows(details: StageDetail[]) {
  return details.map((item) => [
    <span style={{ fontWeight: 600 }}>{item.process_stage}<br /><small>{item.document_id ?? item.source_entity_id ?? "—"}</small></span>,
    item.status,
    item.party_name ? `${item.party_name}${item.party_country_code ? ` · ${item.party_country_code}` : ""}` : "—",
    item.origin ?? "—",
    item.destination ?? "—",
    item.expected_at ? new Date(item.expected_at).toLocaleString() : "—",
    item.occurred_at ? new Date(item.occurred_at).toLocaleString() : "Planned",
    item.amount == null ? "—" : formatCompactAmount(item.amount, item.currency),
    item.incoterm ?? item.booking_code ?? "—",
    item.source_system,
  ]);
}

export function WorkflowDetailsModal({ workflowId, onClose }: { workflowId: string; onClose: () => void }) {
  const [tab, setTab] = useState("Summary");
  const [data, setData] = useState<Projection | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/operations/workflows/${workflowId}`, { signal: controller.signal, credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error(`Workflow API returned ${response.status}`);
        return response.json();
      })
      .then(setData)
      .catch((reason) => {
        if (reason.name !== "AbortError") setError(reason.message);
      });
    return () => controller.abort();
  }, [workflowId]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const poComplete = useMemo(() => data?.stage_details.some((detail) =>
    detail.process_stage === "purchase_order" && /complete|created|issued|approved/i.test(detail.status)
  ) ?? false, [data]);

  const visibleEvents = useMemo(
    () => data?.events.filter((event) => matchesTab(event, tab)) ?? [],
    [data, tab],
  );

  return (
    <div className="netiv-workflow-modal__backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="netiv-workflow-modal" role="dialog" aria-modal="true" aria-labelledby="workflow-title">
        <header className="netiv-workflow-modal__header">
          <div>
            <div className="netiv-workflow-modal__eyebrow">Signal-to-Invoice workflow</div>
            <h2 id="workflow-title">Workflow details</h2>
            <div className="netiv-workflow-modal__uuid">{workflowId}</div>
          </div>
          <button className="netiv-workflow-modal__close" onClick={onClose} aria-label="Close workflow details"><X size={20} /></button>
        </header>

        <nav className="netiv-workflow-modal__tabs" aria-label="Workflow process steps">
          {TABS.map((item) => (
            <button key={item} className={item === tab ? "netiv-workflow-tab netiv-workflow-tab--on" : "netiv-workflow-tab"} onClick={() => setTab(item)}>
              {item}
            </button>
          ))}
        </nav>

        <div className="netiv-workflow-modal__body">
          {error && <div className="netiv-workflow-modal__message netiv-down" role="alert">Data unavailable</div>}
          {!data && !error && <div className="netiv-workflow-modal__message" aria-busy="true"/>}
          {data && (
            <>
              <div className="netiv-workflow-modal__summary">
                <div><span>Workflow status</span><strong>{data.workflow.status}</strong></div>
                <div><span>PO milestone</span><Badge tone={poComplete ? "up" : "warn"}>{poComplete ? "CREATED / COMPLETE" : "IN PROGRESS"}</Badge></div>
                <div><span>Recorded events</span><strong>{data.events.length}</strong></div>
                <div><span>Open exceptions</span><strong>{data.exceptions.filter((item) => !["resolved", "waived"].includes(item.status)).length}</strong></div>
              </div>

              {tab === "Summary" && (
                <>
                  <h3>Before and after PO creation</h3>
                  <DataTable
                    columns={["Process event", "Status", "Planned", "Actual", "Source", "Native ID", "Details"]}
                    rows={eventRows(data.events)}
                  />
                  <h3>Structured process details</h3>
                  <DataTable
                    columns={["Stage / document", "Status", "Party / origin", "Origin", "Destination", "Planned", "Actual", "Amount", "Terms / booking", "Source"]}
                    rows={stageRows(data.stage_details)}
                  />
                  <h3>Cross-system references</h3>
                  <DataTable
                    columns={["Source system", "Record type", "Native identifier"]}
                    rows={data.external_references.map((item) => [item.source_system, item.source_entity_type, item.source_entity_id])}
                  />
                </>
              )}

              {tab !== "Summary" && tab !== "Exceptions" && (
                <>
                  <DataTable
                    columns={["Stage / document", "Status", "Party / origin", "Origin", "Destination", "Planned", "Actual", "Amount", "Terms / booking", "Source"]}
                    rows={stageRows(data.stage_details.filter((item) => TAB_STAGES[tab]?.includes(item.process_stage)))}
                  />
                  {!data.stage_details.some((item) => TAB_STAGES[tab]?.includes(item.process_stage)) && (
                    <DataTable
                      columns={["Process event", "Status", "Planned", "Actual", "Source", "Native ID", "Details"]}
                      rows={eventRows(visibleEvents)}
                    />
                  )}
                </>
              )}

              {tab === "Exceptions" && (
                <>
                <DataTable
                  columns={["Exception", "Stage", "Severity", "Status", "Owner", "Detected"]}
                  rows={data.exceptions.map((item) => [
                    <span style={{ fontWeight: 600 }}>{item.exception_code}<br /><small>{item.description ?? ""}</small></span>,
                    item.process_stage,
                    <Badge tone={item.severity === "critical" || item.severity === "high" ? "down" : "warn"}>{item.severity}</Badge>,
                    item.status,
                    item.owner_team ?? "Unassigned",
                    new Date(item.detected_at).toLocaleString(),
                  ])}
                />
                <h3>Action and acknowledgement history</h3>
                <DataTable
                  columns={["Action", "Status", "Assigned team", "External system", "Ticket", "Requested", "Acknowledged", "Error"]}
                  rows={data.actions.map((item) => [
                    item.action_type,
                    item.status,
                    item.assigned_team ?? "—",
                    item.external_system ?? "NETIV",
                    item.external_ticket_id ?? "—",
                    new Date(item.requested_at).toLocaleString(),
                    item.acknowledged_at ? new Date(item.acknowledged_at).toLocaleString() : "Pending",
                    item.last_error ?? "—",
                  ])}
                />
                </>
              )}
            </>
          )}
        </div>

        <footer className="netiv-workflow-modal__footer">
          <button className="netiv-workflow-modal__button" onClick={onClose}>Close</button>
        </footer>
      </section>
    </div>
  );
}
